import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
// Third Party
import { BsModalRef, BsModalService, ModalOptions } from 'ngx-bootstrap/modal';
import { Subscription } from 'rxjs';
import { format } from 'date-fns';
// Ag-Grid
import { GridOptions, GridApi, ColDef } from 'ag-grid-community';
import { agGridDarkTheme } from 'src/app/shared/components/ag-grid/dark-theme';
import { AgGridAngular } from 'ag-grid-angular';
// models
import { DispoColumnDef } from './columnDefs';
import { DispoList } from '../dispositions/disposition/models';
import {
  DateFormats,
  DispositionTypes,
  ModalID,
  State,
} from '../shared/models/constants';
import {
  BoundBookEntry,
  UpdatedBoundBookResult,
  ReservedFirearm,
  ReservedInputs,
  Form4473,
  Form4473Transferee,
  Form4473Transferor,
} from '../shared/models/general';
//components
import { CheckboxRenderer } from '../shared/components/ag-grid/checkbox-renderer.component';
import { ReserveInputsComponent } from './modals/reserve-inputs/reserve-inputs.component';
// Services
import { SharedService } from '../shared/services/shared.service';
import { AccountingService } from '../accounting/services/accounting.service';
import { createForm4473FFL, createForm4473Firearm } from '../shared/functions';

@Component({
  selector: 'app-disposition-selection',
  templateUrl: './disposition-selection.component.html',
  styleUrls: ['./disposition-selection.component.scss'],
  standalone: true,
  imports: [AgGridAngular],
})
export class DispositionSelectionComponent {
  private destroyRef = inject(DestroyRef);
  subscriptions = new Subscription();
  rowData: DispoList[] = [];
  reserved_inputs: ReservedInputs = new ReservedInputs();
  gridApi: GridApi;

  columnDefs: ColDef[] = DispoColumnDef;
  defaultColDef;
  detailCellRendererParams: any;
  overlayLoadingTemplate;
  overlayNoRowsTemplate;
  gridOptions: GridOptions = {
    theme: agGridDarkTheme,
    // rowMultiSelectWithClick: true, // Deprecated
    rowModelType: 'clientSide', // Ensure client-side row model is used
    headerHeight: 50,
    rowHeight: 25,
    context: { ConfigComponent: this }, // this allows access to component functions
    rowClass: 'ag-grid-row',
  };
  components: any;
  theme = agGridDarkTheme;

  constructor(
    private router: Router,
    public sharedService: SharedService,
    public accountingService: AccountingService,
    private modalService: BsModalService,
  ) {
    this.overlayLoadingTemplate =
      '<span style="padding: 10px; border: 2px solid #444;background: #2399e5; color: white;"><i class="fa fa-spinner fa-spin"></i> Finding Disposition Records...</span>';
    this.overlayNoRowsTemplate =
      '<span style="padding: 10px; border: 2px solid #444;background: #2399e5; color: white;"><i class="fa fa-spinner fa-spin"></i> No Records Found!</span>';

    this.defaultColDef = {
      sortable: true,
      filter: true,
      resizable: true,
      floatingFilter: true,
      wrapHeaderText: true,
    };
    this.components = {
      checkboxRenderer: CheckboxRenderer,
    };
    this.ensureCheckboxColumn();
    // bound_book_retrieved$
    this.subscriptions.add(
      this.sharedService.bound_book_retrieved$
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((response) => {
          this.sharedService.closeStatusSpinner();
          this.createDispositionList();
        }),
    );
    // bound_book_updated$
    this.subscriptions.add(
      this.sharedService.bound_book_updated$
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((response: UpdatedBoundBookResult) => {
          // Update all rows in rowData that are in the updated bound book
          for (const updated_firearm of response.bound_book) {
            this.updateStateValue(
              updated_firearm.firearm_id,
              updated_firearm.state,
            );
          }
        }),
    );
    // reserve_firearm_inputs_captured$
    this.subscriptions.add(
      this.sharedService.reserve_firearm_inputs_captured$
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(async (response: ReservedInputs) => {
          console.log(response);
          this.reserved_inputs = response as ReservedInputs;
          await this.addItemsToReservedList();
          this.sharedService.selected_firearms_for_disposition = [];
          await this.getReservedItems();
          this.createDispositionList();
        }),
    );
    // reserve_firearm_inputs_canceled$
    this.subscriptions.add(
      this.sharedService.reserve_firearm_inputs_canceled$
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(async (response: ReservedInputs) => {
          this.reserved_inputs = response as ReservedInputs;
          await this.revertStateOfSelectedItems(this.reserved_inputs);
          this.createDispositionList();
        }),
    );
  }

  private ensureCheckboxColumn() {
    const hasSelected = this.columnDefs.some(
      (column) => column.headerName === 'Selected',
    );
    if (!hasSelected) {
      this.columnDefs = [
        ...this.columnDefs,
        {
          headerName: 'Selected',
          field: 'select_item',
          resizable: false,
          width: 90,
          pinned: 'right',
          hide: false,
          lockVisible: true,
          filter: false,
          sortable: false,
          cellRenderer: 'checkboxRenderer',
        },
      ];
    }
  }
  // Modals
  modalRef: BsModalRef;
  modalConfig = { animated: true };

  modalOptions: ModalOptions = {
    backdrop: 'static',
    keyboard: false,
    class: 'modal-dialog  modal-dialog-centered',
  } as any;
  onGridReady(params: any) {
    this.gridApi = params.api;
  }
  async ngOnInit() {
    await this.sharedService.getBoundBookEntries();
  }
  createDispositionList() {
    this.rowData = [];
    const reservedFirearmIds = new Set(
      this.sharedService.reserved_firearm_list.map((item) => item.firearm_id),
    );
    if (this.sharedService.bound_book.length > 0) {
      for (const bb_entry of this.sharedService.bound_book.filter(
        (item) =>
          item.state !== State.disposition &&
          !reservedFirearmIds.has(item.firearm_id),
      )) {
        const dispo_item = new DispoList();
        if (bb_entry.state !== State.acquisition) {
          dispo_item.selectable = false;
        }
        dispo_item.date_acquired = format(
          bb_entry.a_date_acquired,
          DateFormats.M_D_Y,
        );
        dispo_item.firearm_id = bb_entry.firearm_id;
        dispo_item.bound_book_id =
          this.sharedService.selected_boundbook.boundbook_id;
        dispo_item.received_from = bb_entry.a_received_from_name;
        dispo_item.serial_number = bb_entry.a_serial_number;
        dispo_item.state_abbreviation = bb_entry.state.substring(0, 1);
        dispo_item.state = bb_entry.state;
        dispo_item.select_item =
          this.sharedService.selected_firearms_for_disposition.some(
            (f) => f.firearm_id === bb_entry.firearm_id,
          );
        if (
          bb_entry.a_importer &&
          bb_entry.a_importer !== '' &&
          bb_entry.a_importer !== 'N/A'
        ) {
          dispo_item.firearm =
            bb_entry.a_importer +
            ' - ' +
            bb_entry.a_model +
            ' - ' +
            bb_entry.a_caliber_gauge;
        } else {
          dispo_item.firearm =
            bb_entry.a_manufacture +
            ' - ' +
            bb_entry.a_model +
            ' - ' +
            bb_entry.a_caliber_gauge;
        }
        this.rowData.push(dispo_item);
      }
      this.rowData = [...this.rowData];
    }
  }
  cellSingleClicked(params: any) {
    if (params.data['selectable'] === true) {
      for (const row of this.rowData) {
        if (row.firearm_id === params.data.firearm_id) {
          row.select_item = !row.select_item;
          if (row.select_item === true) {
            // Storing selected firearms for dispo in the service
            this.sharedService.selected_firearms_for_disposition.push(row);
            this.selectCheckbox(params.node.rowIndex);
          } else {
            this.sharedService.selected_firearms_for_disposition =
              this.sharedService.selected_firearms_for_disposition.filter(
                (item) => item.firearm_id !== row.firearm_id,
              );
            this.unselectCheckbox(params.node.rowIndex);
          }
        }
      }
    } else {
      this.sharedService.raiseWarningToast(
        'Item is not available for disposition',
      );
    }
  }

  onCheckboxToggled(params: any) {
    this.cellSingleClicked(params);
  }
  selectCheckbox(rowIndex: number): void {
    const rowNode = this.gridApi.getRowNode(rowIndex.toString());
    if (rowNode) {
      rowNode.setDataValue('select_item', true);
    }
  }
  unselectCheckbox(rowIndex: number): void {
    const rowNode = this.gridApi.getRowNode(rowIndex.toString());
    if (rowNode) {
      rowNode.setDataValue('select_item', false);
    }
  }
  lostStolenItemClick() {
    // need to complete this function
    console.log('an item was lost or stolen');
  }
  destroyItemClick() {
    // need to complete this function
    console.log('an item was lost or stolen');
  }
  clearSelectionsClick() {
    this.sharedService.selected_firearms_for_disposition = [];
    for (const row of this.rowData) {
      if (row.select_item === true) {
        row.select_item = false;
      }
    }
    this.rowData = [...this.rowData];
  }
  async refreshTableDataClick() {
    this.sharedService.openStatusSpinner('Refreshing Disposition Records');
    await this.sharedService.getBoundBookEntries();
    this.sharedService.closeStatusSpinner();
    this.createDispositionList();
  }
  async changeStateOfSelectedItems(): Promise<void> {
    for (const firearm of this.sharedService
      .selected_firearms_for_disposition) {
      let bound_book_entry = this.sharedService.bound_book.find(
        (entry) => entry.firearm_id === firearm.firearm_id,
      );
      if (bound_book_entry) {
        try {
          const response = await this.sharedService.getBoundBookEntryById(
            bound_book_entry._id,
          );
          if (response.state === State.acquisition) {
            bound_book_entry.state = State.in_progress;
            await this.sharedService.updateBoundBookEntry(bound_book_entry);
          }
        } catch (error) {
          console.error('Error fetching bound book entry:', error);
        }
      }
    }
  }
  async revertStateOfSelectedItems(inputs: ReservedInputs): Promise<void> {
    for (const firearm of this.sharedService
      .selected_firearms_for_disposition) {
      let bound_book_entry = this.sharedService.bound_book.find(
        (entry) => entry.firearm_id === firearm.firearm_id,
      );
      if (bound_book_entry) {
        try {
          const response = await this.sharedService.getBoundBookEntryById(
            bound_book_entry._id,
          );
          if (
            bound_book_entry.state === State.in_progress ||
            response.state === State.in_progress
          ) {
            bound_book_entry.state = State.acquisition;
            await this.sharedService.updateBoundBookEntry(bound_book_entry);
          }
        } catch (error) {
          console.error('Error fetching bound book entry:', error);
        }
      }
    }
  }
  async reserveItemsClick() {
    await this.changeStateOfSelectedItems();
    this.openReserveInputsModal();
  }
  async addItemsToReservedList(reserved_for?: string) {
    const reserved_items: ReservedFirearm[] = [];
    const reservation_id = await this.createReservationId();
    for (const firearm of this.sharedService
      .selected_firearms_for_disposition) {
      const reserved_firearm = new ReservedFirearm();
      reserved_firearm.bound_book_id = firearm.bound_book_id;
      reserved_firearm.reservation_id = reservation_id;
      reserved_firearm.firearm_id = firearm.firearm_id;
      reserved_firearm.firearm = firearm.firearm;
      reserved_firearm.serial_number = firearm.serial_number;
      reserved_firearm.reserved_date = format(new Date(), DateFormats.M_D_Y);
      reserved_firearm.reserved_to = this.reserved_inputs.reserved_for
        ? this.reserved_inputs.reserved_for
        : reserved_for;
      reserved_firearm.reserved_by = this.sharedService.logged_in_user.username;
      reserved_firearm.notes = this.reserved_inputs.reserve_notes;
      reserved_items.push(reserved_firearm);
    }
    await this.sharedService.addFirearmToReservedList(reserved_items);
    // addFirearmToReservedList may re-fetch from the server (when online),
    // which can overwrite the optimistic add if the POST hasn't been processed
    // yet.  Re-merge the items so they are guaranteed to be in the list.
    const existingIds = new Set(
      this.sharedService.reserved_firearm_list.map((f) => f.firearm_id),
    );
    const missingItems = reserved_items.filter(
      (item) => !existingIds.has(item.firearm_id),
    );
    if (missingItems.length > 0) {
      this.sharedService.reserved_firearm_list = [
        ...this.sharedService.reserved_firearm_list,
        ...missingItems,
      ];
    }
    this.sharedService.selected_reservation_id = reservation_id;
    // this.sharedService.selected_firearms_for_disposition = []; //don't think it's needed
  }
  async createReservationId(): Promise<number> {
    await this.getReservedItems();
    if (this.sharedService.reserved_firearm_list.length === 0) {
      return 1000;
    } else {
      const max_reservation_id = this.findMaxReservationId() + 1;
      return max_reservation_id;
    }
  }
  findMaxReservationId(): number {
    if (this.sharedService.reserved_firearm_list.length === 0) {
      return 0; // or any default value you prefer
    }

    return this.sharedService.reserved_firearm_list.reduce((maxId, firearm) => {
      return firearm.reservation_id > maxId ? firearm.reservation_id : maxId;
    }, 0);
  }
  async getReservedItems() {
    await this.sharedService.getReservedFirearmList();
    this.sharedService.reserved_firearm_list = [
      ...this.sharedService.reserved_firearm_list,
    ];
  }
  async dispoToIndividualClick() {
    this.sharedService.loading = true;
    this.sharedService.loading_message = 'Loading Disposition...';
    await this.changeStateOfSelectedItems();
    await this.addItemsToReservedList(DispositionTypes.individual);
    await this.createNewForm4473();
    this.sharedService.selected_firearms_for_disposition = [];
    this.sharedService.changeRoute('buyer-form-4473');
  }
  async dispoToFflClick() {
    this.sharedService.loading = true;
    this.sharedService.loading_message = 'Loading Disposition...';
    await this.changeStateOfSelectedItems();
    await this.addItemsToReservedList(DispositionTypes.ffl);
    this.sharedService.selected_firearms_for_disposition = [];
    // Emit BEFORE navigating so the ReplaySubject has the correct value
    // when the DispositionComponent subscribes during route initialisation.
    this.sharedService.reserved_firearm_selected$.next(
      this.sharedService.selected_reservation_id,
    );
    this.sharedService.changeRoute('disposition');
  }
  openHome() {
    this.sharedService.changeRoute('home');
  }
  updateStateValue(firearm_id: number, newState: string) {
    const updatedRow = this.rowData.find(
      (row) => row.firearm_id === firearm_id,
    );
    if (updatedRow) {
      updatedRow.state = newState;
      updatedRow.state_abbreviation = newState.substring(0, 1);
      this.gridApi.applyTransaction({ update: [updatedRow] });
      this.rowData = [...this.rowData];
    }
  }
  uncheckAllItems() {
    for (const row of this.rowData) {
      if (row.select_item === true) {
        row.select_item = false;
        row.selectable = false;
      }
    }
    this.rowData = [...this.rowData];
  }
  openReserveInputsModal() {
    this.modalOptions = {
      initialState: {} as any,
      ignoreBackdropClick: true,
      keyboard: false,
      class: 'modal-dialog, modal-dialog-centered',
    };
    this.modalRef = this.modalService.show(
      ReserveInputsComponent,
      Object.assign({ id: ModalID.reserve_inputs }, this.modalOptions),
    );
  }
  getCustomerDisplayName(customerId: string): string {
    const customer = this.accountingService.customers.find(
      (c) => c.id === customerId,
    );
    return customer ? customer.contact.display_name : 'Customer not found';
  }
  customerDisplayNameRenderer(params): string {
    return this.getCustomerDisplayName(params.value);
  }
  async createNewForm4473() {
    let new_4473: Form4473 = new Form4473();
    new_4473.company_id = this.sharedService.company_info.company_id;
    new_4473.ffl = createForm4473FFL(this.sharedService);
    new_4473.firearms = createForm4473Firearm(this.sharedService);
    new_4473.transferee = new Form4473Transferee();
    new_4473.transferor = new Form4473Transferor();
    new_4473.transaction_serial_number = null;
    await this.sharedService.addForm4473(new_4473);
  }
  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
